import { SortingState } from "@tanstack/react-table";
import TableContainer from "Common/TableContainer";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { QuotationTrashActionMenu } from "Components/MenuActionButton/QuotationTrashActionMenu";
import { app_modules } from "helpers/apps_modules_list";
import { formatDate } from "helpers/functions/formatDate";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";

import { fetchLatestPermissions } from "slices/auth/login/thunk";
import {
  fetchQuotationTrash,
  revertQuotationById,
} from "slices/Quotation/thunk";
import Swal from "sweetalert2";

type TrashLogProps = {
  handleTabChange?: (tabIndex: number) => void;
  searchInput: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  search: string;
  colValue?: any;
  setColValue?: React.Dispatch<React.SetStateAction<any>>;
};

const TrashLog: React.FC<TrashLogProps> = ({
  handleTabChange,
  searchInput,
  setSearch,
  search,
  colValue,
  setColValue,
}) => {
  document.title = "Quotation Trash Log";
  const dispatch = useDispatch<any>();

  const selectQuotation = createSelector(
    (state: any) => state.QuotationReducer || [],
    (quotation) => ({
      quotationTrashList: quotation.trashList,
      rowCount: quotation.rowCount,
      loading: quotation.loading,
      error: quotation.error,
    })
  );

  const { quotationTrashList, rowCount, loading, error } =
    useSelector(selectQuotation);

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page_trash") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);

  // Save current page to sessionStorage when component mounts
  useEffect(() => {
    if (pageParam > 0) {
      sessionStorage.setItem("trashPage", pageParam.toString());
    } else {
      // Clear sessionStorage when navigating from sidebar (page 0)
      sessionStorage.removeItem("trashPage");
    }
  }, [pageParam]);

  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<any>();
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.quotation?.app_name
  );
  const quotation_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.quotation?.modules?.quotation
  );

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      fetchQuotationTrash({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    // colValue,
    sortField,
    sortDirection,
  ]);

  const prevFilters = useRef(columnFilters);
  
  useEffect(() => {
    if (JSON.stringify(prevFilters.current) !== JSON.stringify(columnFilters)) {
      prevFilters.current = columnFilters;
      dispatch(
        fetchQuotationTrash({
          page: 1, // Always use page 1 when filters change
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: colValue,
        })
      );
    }
  }, [colValue]);

  useEffect(() => {
    setData(quotationTrashList);
  }, [quotationTrashList]);

  // Sync pageIndex with URL parameters
  useEffect(() => {
    const currentPageParam = parseInt(searchParams.get("page_trash") || "0");
    if (currentPageParam !== pageIndex) {
      setPageIndex(currentPageParam);
    }
  }, [searchParams, pageIndex]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);

  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page_trash: newPage.toString() });
    // Save current page to sessionStorage for back navigation
    sessionStorage.setItem("trashPage", newPage.toString());
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  //*// Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  const handleRevert = useCallback(
    async (id: any) => {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Are You Sure You Want To Revert Quotation!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, revert it!",
        width: "350px",
        customClass: {
          popup: "swal2-sm-popup",
        },
      });

      if (!result.isConfirmed) return;

      await dispatch(revertQuotationById(id))
        .unwrap()
        .then(() => {
          toast.success(`Quotation Reverted successfully!!`);

          if (data.length === 1 && pageIndex > 0) {
            handlePageChange(pageIndex - 1);
          } else {
            dispatch(
              fetchQuotationTrash({
                page: pageIndex + 1,
                pageSize,
                sort: sortField,
                order: sortField ? sortDirection : "",
                search,
                filters: colValue,
              })
            );
          }
        })
        .catch((err: any) => {
          toast.error("Failed to Revert Quotation");
        });
    },
    [dispatch, data, pageIndex]
  );

  const DynamicAction = useMemo(
    () => [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        size: 80,
        cell: ({ row }: any) => (
          <div onClick={(e) => e.stopPropagation()}>
            <QuotationTrashActionMenu
              row={row}
              handleRevert={handleRevert}
              canChange={!!quotation_permissions?.permissions?.Update}
            />
          </div>
        ),
      },
    ],
    [handleRevert, quotation_permissions]
  );

  // const DynamicAction =
  //    quotation_permissions?.permissions?.Update === true ?
  //   useMemo(
  //     () => [
  //       {
  //         header: "Action",
  //         enableColumnFilter: false,
  //         enableSorting: false,
  //         size: 80,
  //         cell: ({ row }: any) => (
  //           <div onClick={(e) => e.stopPropagation()}>
  //             <QuotationTrashActionMenu row={row} handleRevert={handleRevert} />
  //           </div>
  //         ),
  //       },
  //     ],
  //     // [handleDelete]
  //     []
  //   ): [];

  const columns = useMemo(
    () => [
      // {
      //   header: "Sr No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row, table }: { row: any; table: any }) => {
      //     const pageIndex = table.getState().pagination.pageIndex;
      //     const pageSize = table.getState().pagination.pageSize;
      //     const rowIndex = row.index;
      //     return pageIndex * pageSize + rowIndex + 1;
      //   },
      // },
      {
        header: "Quotation Number",
        accessorKey: "quotation_auto_id",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/quotation/trash/column-filter-dropdown/?column_filter=quotation_auto_id",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              maxWidth: "200px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            {row.original.quotation_auto_id || "-"}
          </div>
        ),
      },
      {
        header: "Customer/Company Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/quotation/trash/column-filter-dropdown/?column_filter=customer_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              maxWidth: "200px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            {row.original.customer_details.customer_name || "-"}
          </div>
        ),
      },
      {
        header: "Mobile Number",
        accessorKey: "mobile_no",
        // accessorFn: (row: any) => row.customer_details?.mobile_no || "-",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/customers/column-filter-dropdown/?column_filter=mobile_no",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              maxWidth: "200px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            {row.original.customer_details?.mobile_no || "-"}
          </div>
        ),
      },

      {
        header: "Quotation Date",
        accessorKey: "quotation_date",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const date = formatDate(row.original.quotation_date);
          return `${date}`;
        },
      },
      {
        header: "Validity Date",
        accessorKey: "validity",
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const expiryDateStr = row.original.validity;
          if (!expiryDateStr) return "N/A";

          const expiryDate = new Date(expiryDateStr);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const isExpired = expiryDate < today;
          const formattedDate = formatDate(expiryDateStr);

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "3px",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  color: isExpired ? "#dc3545" : "inherit",
                  fontWeight: isExpired ? 600 : "normal",
                }}
              >
                {formattedDate}
              </span>
              {isExpired && (
                <span
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: "8px",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                  // className="bg-danger"
                >
                  Expired
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "current_status",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => (
          <span
            className={`badge text-white ${
              row.original.current_status === "Draft"
                ? "bg-primary"
                : row.original.current_status === "Sent"
                ? "bg-warning"
                : row.original.current_status === "Won"
                ? "bg-success"
                : row.original.current_status === "Lost"
                ? "bg-danger"
                : "bg-info"
            } text-dark`}
          >
            {row.original.current_status || "-"}
          </span>
        ),
      },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow normal wrapping
              wordBreak: "break-word", // break inside long words if necessary
              maxWidth: "300px", // set a maximum width
              minWidth: "150px", // set a minimum width
              overflowWrap: "break-word", // allow break at any point if needed
            }}
          >
            {row.original.notes || "-"}
          </div>
        ),
      },
      ...DynamicAction,
    ],
    [
      dispatch,
      quotationTrashList,
      quotation_permissions,
      DynamicAction,
      pageIndex,
      pageSize,
    ]
  );

  return (
    <LoaderErrorWrapper loading={loading} error={error}>
      <TableContainer
        columns={columns}
        data={data}
        totalRecords={rowCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        // onPageChange={setPageIndex}
        onPageChange={handlePageChange} // for persisted pagination across pages
        onPageSizeChange={setPageSize}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        colValue={setColValue}
        sorting={sorting}
        onSortingChange={setSorting}
        isBordered={false}
        isPagination={true}
        isGlobalFilter={false}
        SearchPlaceholder="Search ..."
        manualSorting={true}
      />
    </LoaderErrorWrapper>
  );
};

export default TrashLog;
